import { baseApi } from './baseApi';
import { Debt, DebtUpdateRequest } from 'models/Debt';
import { BillResponse } from 'models/SaleOrder';
import { QueryParams, TAG_TYPES } from 'types';
import { getQueryParams } from 'utils';

const URL_DEBT = 'debts';

const debtApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDebts: builder.query<
      {
        data: {
          rows: Debt[];
          count: number;
          maxDebt: number;
          debtRefund: number;
          pendingdebts: number;
          completedebts: number;
          overduedebts: number;
          refunddebts: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const query = getQueryParams(queryParams);
        if (query === '') return { url: URL_DEBT, method: 'GET' };
        return {
          url: `${URL_DEBT}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.rows || []).map(({ id }) => ({ type: TAG_TYPES.Debt, id })),
              { type: TAG_TYPES.Debt, id: 'LIST' },
            ]
          : [{ type: TAG_TYPES.Debt, id: 'LIST' }];
      },
    }),

    getDebtById: builder.query<
      {
        data: Debt;
        success: boolean;
      },
      {
        id: string;
        query?: QueryParams;
      }
    >({
      query: (params) => {
        const queryString = params.query ? `?${getQueryParams(params.query)}` : '';
        return {
          url: `${URL_DEBT}/${params.id}${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, params) => [{ type: TAG_TYPES.Debt, id: params.id }],
    }),

    createDebt: builder.mutation<Debt, Omit<Debt, 'id'>>({
      query: (data) => ({
        url: URL_DEBT,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.Debt, id: 'LIST' }],
    }),

    updateDebt: builder.mutation<Debt, DebtUpdateRequest & { id: string }>({
      query: ({ id, ...patch }) => ({
        url: `${URL_DEBT}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Debt, id },
        { type: TAG_TYPES.Debt, id: 'LIST' },
      ],
    }),

    deleteDebt: builder.mutation<void, string>({
      query: (id) => ({
        url: `${URL_DEBT}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPES.Debt, id },
        { type: TAG_TYPES.Debt, id: 'LIST' },
      ],
    }),
    createDebtDocument: builder.mutation<BillResponse, { id: string }>({
      query: ({ id }) => ({
        url: `${URL_DEBT}/${id}/documents`,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDebtsQuery,
  useLazyGetDebtsQuery,
  useGetDebtByIdQuery,
  useCreateDebtMutation,
  useUpdateDebtMutation,
  useDeleteDebtMutation,
  useCreateDebtDocumentMutation,
} = debtApiService;
